/**
 * Admin Scholarship Management Component
 * Allows admins to view, add, edit, and delete scholarships
 */
import { useState, useEffect } from 'react';
import { 
  Award, Plus, Search, Edit, Trash2, Loader2, 
  ExternalLink, Eye, Users, Sparkles, X, Save,
  Globe, GraduationCap, DollarSign, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';
import { scholarshipAPI } from '@/lib/api';

const COUNTRIES = [
  'United Kingdom', 'United States', 'Australia', 'Canada', 
  'Germany', 'France', 'Netherlands', 'Ireland', 'Spain',
  'Italy', 'Sweden', 'Denmark', 'Norway', 'Switzerland',
  'Multiple European Countries'
];

const DEGREE_LEVELS = ['Undergraduate', 'Masters', 'PhD'];
const FUNDING_TYPES = ['Full', 'Partial', 'Tuition Only', 'Living Expenses'];

const emptyScholarship = {
  name: '',
  provider: '',
  description: '',
  eligibility: '',
  funding_type: 'Partial',
  funding_amount: '',
  countries: [],
  eligible_nationalities: [],
  degree_levels: [],
  fields_of_study: [],
  deadline: '',
  application_url: '',
  official_website: '',
  benefits: [],
  requirements: [],
  is_active: true,
  featured: false,
  tags: []
};

const AdminScholarshipManager = () => {
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [editingScholarship, setEditingScholarship] = useState(null);
  const [deleteScholarship, setDeleteScholarship] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState(emptyScholarship);
  const [saving, setSaving] = useState(false);
  
  // AI fetch state
  const [aiQuery, setAiQuery] = useState('');
  const [aiFetching, setAiFetching] = useState(false);

  useEffect(() => {
    loadScholarships();
  }, []);

  const loadScholarships = async () => {
    setLoading(true);
    try {
      const res = await scholarshipAPI.getAll({ limit: 100 });
      setScholarships(res.data.scholarships || []);
    } catch (error) {
      toast.error('Failed to load scholarships');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData(emptyScholarship);
    setEditingScholarship(null);
    setShowAddModal(true);
  };

  const handleEdit = (scholarship) => {
    setFormData({
      ...emptyScholarship,
      ...scholarship,
      countries: scholarship.countries || [],
      degree_levels: scholarship.degree_levels || [],
      benefits: scholarship.benefits || [],
      requirements: scholarship.requirements || [],
      tags: scholarship.tags || []
    });
    setEditingScholarship(scholarship);
    setShowAddModal(true);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.name || !formData.provider || !formData.description) {
      toast.error('Please fill in name, provider, and description');
      return;
    }
    if (formData.countries.length === 0) {
      toast.error('Please select at least one country');
      return;
    }
    if (formData.degree_levels.length === 0) {
      toast.error('Please select at least one degree level');
      return;
    }

    setSaving(true);
    try {
      if (editingScholarship) {
        await scholarshipAPI.update(editingScholarship.id, formData);
        toast.success('Scholarship updated successfully');
      } else {
        await scholarshipAPI.create(formData);
        toast.success('Scholarship created successfully');
      }
      setShowAddModal(false);
      loadScholarships();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save scholarship');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteScholarship) return;
    
    try {
      await scholarshipAPI.delete(deleteScholarship.id);
      toast.success('Scholarship deleted successfully');
      setDeleteScholarship(null);
      loadScholarships();
    } catch (error) {
      toast.error('Failed to delete scholarship');
    }
  };

  const handleAIFetch = async () => {
    if (!aiQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    setAiFetching(true);
    try {
      const res = await scholarshipAPI.aiFetch(aiQuery);
      toast.success(res.data.message);
      setShowAIModal(false);
      setAiQuery('');
      loadScholarships();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI fetch failed');
    } finally {
      setAiFetching(false);
    }
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => {
      const arr = prev[field] || [];
      if (arr.includes(item)) {
        return { ...prev, [field]: arr.filter(i => i !== item) };
      } else {
        return { ...prev, [field]: [...arr, item] };
      }
    });
  };

  const filteredScholarships = scholarships.filter(s => 
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.provider?.toLowerCase().includes(search.toLowerCase())
  );

  const getFundingBadgeColor = (type) => {
    switch (type) {
      case 'Full': return 'bg-green-100 text-green-600 border-green-500/30';
      case 'Partial': return 'bg-teal-100 text-teal-600 border-yellow-500/30';
      case 'Tuition Only': return 'bg-blue-100 text-blue-600 border-blue-500/30';
      default: return 'bg-gray-200 text-gray-500 border-slate-500/30';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <Award className="w-6 h-6 text-teal-600" />
            Scholarship Management
          </h2>
          <p className="text-sm text-gray-500">{scholarships.length} scholarships in database</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowAIModal(true)}
            variant="outline"
            className="border-purple-500/50 text-purple-600 hover:bg-purple-500/10"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            AI Fetch
          </Button>
          <Button
            onClick={handleAdd}
            className="bg-teal-500 hover:bg-yellow-600 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Scholarship
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
        <Input
          type="text"
          placeholder="Search scholarships..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-[#e9ecef]/50 border-gray-200 text-gray-800"
        />
      </div>

      {/* Scholarships List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : filteredScholarships.length === 0 ? (
        <div className="text-center py-12">
          <Award className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-gray-500">No scholarships found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredScholarships.map((scholarship) => (
            <div 
              key={scholarship.id}
              className="bg-white border border-gray-200 rounded-xl rounded-lg p-4 flex items-center justify-between gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-800 truncate">{scholarship.name}</h3>
                  {scholarship.featured && (
                    <Badge className="bg-teal-100 text-teal-600 border-yellow-500/30 text-xs">
                      Featured
                    </Badge>
                  )}
                  <Badge className={getFundingBadgeColor(scholarship.funding_type)}>
                    {scholarship.funding_type}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 truncate">{scholarship.provider}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <Globe className="w-3 h-3" />
                    {scholarship.countries?.slice(0, 2).join(', ')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {scholarship.views || 0} views
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {scholarship.inquiries || 0} inquiries
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(scholarship)}
                  className="text-gray-500 hover:text-gray-800"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteScholarship(scholarship)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl bg-[#f1f3f5] border-gray-200 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800">
              {editingScholarship ? 'Edit Scholarship' : 'Add New Scholarship'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-600 block mb-1">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Chevening Scholarships"
                  className="bg-[#e9ecef]/50 border-gray-200 text-gray-800"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-600 block mb-1">Provider *</label>
                <Input
                  value={formData.provider}
                  onChange={(e) => setFormData({...formData, provider: e.target.value})}
                  placeholder="e.g., UK Government"
                  className="bg-[#e9ecef]/50 border-gray-200 text-gray-800"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Description *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the scholarship..."
                className="bg-[#e9ecef]/50 border-gray-200 text-gray-800 min-h-[80px]"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">Eligibility</label>
              <Textarea
                value={formData.eligibility}
                onChange={(e) => setFormData({...formData, eligibility: e.target.value})}
                placeholder="Who can apply for this scholarship..."
                className="bg-[#e9ecef]/50 border-gray-200 text-gray-800 min-h-[60px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Funding Type</label>
                <Select value={formData.funding_type} onValueChange={(v) => setFormData({...formData, funding_type: v})}>
                  <SelectTrigger className="bg-[#e9ecef]/50 border-gray-200 text-gray-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FUNDING_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Funding Amount</label>
                <Input
                  value={formData.funding_amount}
                  onChange={(e) => setFormData({...formData, funding_amount: e.target.value})}
                  placeholder="e.g., Full tuition + £1,200/month"
                  className="bg-[#e9ecef]/50 border-gray-200 text-gray-800"
                />
              </div>
            </div>

            {/* Countries */}
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">Study Countries *</label>
              <div className="flex flex-wrap gap-2">
                {COUNTRIES.map(c => (
                  <Badge
                    key={c}
                    variant={formData.countries?.includes(c) ? 'default' : 'outline'}
                    className={`cursor-pointer transition-colors ${
                      formData.countries?.includes(c) 
                        ? 'bg-teal-500 text-black hover:bg-yellow-600' 
                        : 'border-gray-300 text-gray-500 hover:border-slate-500'
                    }`}
                    onClick={() => toggleArrayItem('countries', c)}
                  >
                    {c}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Degree Levels */}
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">Degree Levels *</label>
              <div className="flex flex-wrap gap-2">
                {DEGREE_LEVELS.map(d => (
                  <Badge
                    key={d}
                    variant={formData.degree_levels?.includes(d) ? 'default' : 'outline'}
                    className={`cursor-pointer transition-colors ${
                      formData.degree_levels?.includes(d) 
                        ? 'bg-blue-500 text-white hover:bg-blue-600' 
                        : 'border-gray-300 text-gray-500 hover:border-slate-500'
                    }`}
                    onClick={() => toggleArrayItem('degree_levels', d)}
                  >
                    {d}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Deadline</label>
                <Input
                  value={formData.deadline}
                  onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                  placeholder="e.g., November 2026 or Rolling"
                  className="bg-[#e9ecef]/50 border-gray-200 text-gray-800"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Official Website</label>
                <Input
                  value={formData.official_website}
                  onChange={(e) => setFormData({...formData, official_website: e.target.value})}
                  placeholder="https://..."
                  className="bg-[#e9ecef]/50 border-gray-200 text-gray-800"
                />
              </div>
            </div>

            {/* Options */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                  className="w-4 h-4 rounded border-gray-300 bg-[#e9ecef] text-teal-600 focus:ring-yellow-500"
                />
                <span className="text-sm text-gray-600">Featured Scholarship</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-4 h-4 rounded border-gray-300 bg-[#e9ecef] text-green-500 focus:ring-green-500"
                />
                <span className="text-sm text-gray-600">Active</span>
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-gray-200">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-500 hover:bg-yellow-600 text-black">
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Scholarship</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Fetch Modal */}
      <Dialog open={showAIModal} onOpenChange={setShowAIModal}>
        <DialogContent className="max-w-md bg-[#f1f3f5] border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI Scholarship Discovery
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-500">
              Use AI to search and add scholarships from the web. Enter a query like:
            </p>
            <ul className="text-sm text-gray-400 space-y-1 list-disc list-inside">
              <li>&quot;Free study scholarships in Italy&quot;</li>
              <li>&quot;German DAAD scholarships for masters&quot;</li>
              <li>&quot;PhD funding opportunities in Canada&quot;</li>
              <li>&quot;Pakistani students UK scholarships&quot;</li>
            </ul>
            
            <Textarea
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              placeholder="Enter your scholarship search query..."
              className="bg-[#e9ecef]/50 border-gray-200 text-gray-800 min-h-[80px]"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAIModal(false)} className="border-gray-200">
              Cancel
            </Button>
            <Button 
              onClick={handleAIFetch} 
              disabled={aiFetching}
              className="bg-purple-500 hover:bg-purple-600 text-gray-800"
            >
              {aiFetching ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Fetch Scholarships</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteScholarship} onOpenChange={() => setDeleteScholarship(null)}>
        <AlertDialogContent className="bg-[#f1f3f5] border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Delete Scholarship</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Are you sure you want to delete &quot;{deleteScholarship?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-200">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminScholarshipManager;
